import { useEffect, useState } from 'react';
import { Edit, Plus, Power } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { PageContainer } from '../../../shared/components/PageContainer';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/lib/permissions';
import { mensagemDeErro } from '../api';
import { LeaveTypeItem } from '../api/afastamentos';
import { LEAVE_TARGET_OPTIONS, VACATION_EFFECT_OPTIONS, labelOf } from '../components/format';
import { PessoasModal } from '../components/PessoasModal';
import { useSalvarTipoAfastamento, useSituacaoTipoAfastamento, useTiposAfastamento } from '../hooks/afastamentos';

const FLAGS: Array<[keyof LeaveTypeItem, string]> = [
  ['requiresCid', 'Exige CID'],
  ['requiresDocument', 'Exige documento'],
  ['notesRequired', 'Observação obrigatória'],
  ['allowsExtension', 'Permite prorrogação'],
  ['allowsEarlyReturn', 'Permite retorno antecipado'],
  ['requiresApproval', 'Exige aprovação'],
  ['blocksAccess', 'Bloqueia acesso ao sistema'],
  ['medical', 'Dado médico (CID e tipo protegidos)'],
  ['integratesPayroll', 'Integra com folha'],
  ['integratesEsocial', 'Integra com eSocial'],
  ['integratesOccupational', 'Integra com medicina ocupacional'],
  ['suspendsProjects', 'Suspende participação em projetos'],
  ['suspendsApprovals', 'Suspende aprovação de fluxos'],
];

/** Tipos de afastamento parametrizáveis (US-GP-008, seção 4). */
export default function TiposAfastamentoPage() {
  const toast = useToast();
  const tipos = useTiposAfastamento(true);
  const situacao = useSituacaoTipoAfastamento();
  const [editing, setEditing] = useState<LeaveTypeItem | 'novo' | null>(null);

  async function alternar(t: LeaveTypeItem) {
    try {
      await situacao.mutateAsync({ id: t.id, active: !t.active });
      toast.success(t.active ? 'Tipo inativado.' : 'Tipo reativado.');
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível alterar a situação.'));
    }
  }

  return (
    <PageContainer
      title="Tipos de afastamento"
      subtitle="Regras de cada afastamento: documentos, CID, situação funcional, bloqueio de acesso e aprovação."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Tipos de afastamento' }]}
      action={
        <Can feature={FEATURES.pessoasTiposAfastamento} action="create">
          <Button onClick={() => setEditing('novo')}><Plus className="mr-2 h-5 w-5" /> Novo tipo</Button>
        </Can>
      }
    >
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Situação destino</th><th className="px-4 py-3">Regras</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(tipos.data ?? []).map((t) => (
              <tr key={t.id} className={t.active ? '' : 'opacity-60'}>
                <td className="px-4 py-3"><p className="font-medium text-gray-900">{t.name}</p><p className="font-mono text-xs text-gray-500">{t.code}</p></td>
                <td className="px-4 py-3">{t.updatesStatus ? labelOf(LEAVE_TARGET_OPTIONS, t.targetStatus) : 'Não altera'}</td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {[...FLAGS.filter(([k]) => t[k]).map(([, l]) => l), t.vacationEffect && t.vacationEffect !== 'nenhum' ? `Férias: ${labelOf(VACATION_EFFECT_OPTIONS, t.vacationEffect)}` : null]
                    .filter(Boolean).join(' · ') || '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <Can feature={FEATURES.pessoasTiposAfastamento} action="edit">
                    <div className="flex justify-end gap-1">
                      <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => setEditing(t)} aria-label={`Editar ${t.name}`}><Edit className="h-4 w-4" /></button>
                      <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => alternar(t)}
                        aria-label={t.active ? `Inativar ${t.name}` : `Reativar ${t.name}`}><Power className="h-4 w-4" /></button>
                    </div>
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TipoModal value={editing} onClose={() => setEditing(null)} onSaved={(m) => { setEditing(null); toast.success(m); }} />
    </PageContainer>
  );
}

const EMPTY: Partial<LeaveTypeItem> = {
  code: '', name: '', updatesStatus: true, targetStatus: 'afastado', allowsExtension: true, allowsEarlyReturn: true,
};

function TipoModal({ value, onClose, onSaved }: { value: LeaveTypeItem | 'novo' | null; onClose: () => void; onSaved: (m: string) => void }) {
  const salvar = useSalvarTipoAfastamento();
  const [form, setForm] = useState<Partial<LeaveTypeItem>>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const editing = value && value !== 'novo' ? value : null;

  useEffect(() => {
    if (!value) return;
    setError(null);
    setForm(editing ? { ...editing } : EMPTY);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    if (!editing && !/^[a-z0-9_]{2,40}$/.test(form.code ?? '')) return setError('Código: 2 a 40 letras minúsculas, números ou "_".');
    if (!form.name?.trim()) return setError('Informe o nome.');
    if (form.updatesStatus && !form.targetStatus) return setError('Informe a situação destino.');
    const { id: _id, active: _active, code, ...rest } = form;
    const payload = { ...rest, targetStatus: form.updatesStatus ? form.targetStatus : null };
    try {
      await salvar.mutateAsync({ id: editing?.id ?? null, payload: editing ? payload : { ...payload, code } });
      onSaved(editing ? 'Tipo atualizado.' : 'Tipo criado.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar o tipo.'));
    }
  }

  return (
    <PessoasModal open={value !== null} size="lg" title={editing ? `Editar — ${editing.name}` : 'Novo tipo de afastamento'}
      submitLabel="Salvar" isSubmitting={salvar.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput label="Código" value={form.code ?? ''} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toLowerCase() }))} />
        <TextInput label="Nome" value={form.name ?? ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.updatesStatus} onChange={(e) => setForm((f) => ({ ...f, updatesStatus: e.target.checked }))} />
          Atualiza a situação funcional
        </label>
        {form.updatesStatus && (
          <AutocompleteSelect label="Situação destino" options={LEAVE_TARGET_OPTIONS} value={form.targetStatus ?? ''}
            onChange={(v) => setForm((f) => ({ ...f, targetStatus: v }))} />
        )}
        <AutocompleteSelect label="Efeito nas férias" options={VACATION_EFFECT_OPTIONS} value={form.vacationEffect ?? 'nenhum'}
          onChange={(v) => setForm((f) => ({ ...f, vacationEffect: v as LeaveTypeItem['vacationEffect'] }))} />
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {FLAGS.map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.checked }))} />
            {label}
          </label>
        ))}
      </div>
      <p className="text-xs text-gray-500">Folha, eSocial, medicina ocupacional, projetos e fluxos ficam marcados para integração futura.</p>
    </PessoasModal>
  );
}
